/**
 * businessReportList 日度：前端直调 PMS，按店缓存 1 天，多组件（房态健康、客源、折扣、渠道分析/信息、出租率、运营效率）共用
 */

import { getBusinessReportListFromThirdParty } from '../../../service/storeRevenueApi';
import type { RevparBusinessReportItem } from '../../../service/storeRevenueApi';
import { formatDateInChina, getYesterdayStrInChina } from '../../../utils/dateChina';
import { setStoreSliceInLocalStorage } from './storeRevenueLocalStorage';

const TTL_MS = 24 * 60 * 60 * 1000; // 1 天

interface CacheEntry {
  list: RevparBusinessReportItem[];
  fetchedAt: number;
  startDate: string;
  endDate: string;
}

const memoryCache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<RevparBusinessReportItem[]>>();
const memoryCacheMonthly = new Map<string, CacheEntry>();
const inFlightMonthly = new Map<string, Promise<RevparBusinessReportItem[]>>();

function cacheKey(storeId: string | number): string {
  const endStr = getYesterdayStrInChina();
  return `businessReportDaily:${String(storeId)}:${endStr}`;
}

function cacheKeyMonthly(storeId: string | number): string {
  const endStr = getYesterdayStrInChina();
  return `businessReportMonthly:${String(storeId)}:${endStr}`;
}

function isValid(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

/** 接口只支持到上一营业日：查询填上一营业日及前 365 天 */
function getDailyRangeInChina(): { startDate: string; endDate: string } {
  const endDateStr = getYesterdayStrInChina();
  const yesterdayStart = new Date(`${endDateStr}T00:00:00+08:00`);
  const startDate = new Date(yesterdayStart.getTime() - 365 * 86400 * 1000);
  const startDateStr = formatDateInChina(startDate);
  return { startDate: startDateStr, endDate: endDateStr };
}

/**
 * 按店获取日度 businessReportList，带内存 + localStorage 缓存，1 天有效；同店并发只发一次请求
 */
export async function getBusinessReportDailyWithCache(
  storeId: string | number
): Promise<RevparBusinessReportItem[]> {
  const key = cacheKey(storeId);
  const entry = memoryCache.get(key);
  if (entry && isValid(entry)) {
    return entry.list;
  }
  const existing = inFlight.get(key);
  if (existing) return existing;

  const { startDate, endDate } = getDailyRangeInChina();
  const promise = getBusinessReportListFromThirdParty(storeId, {
    dateType: 0,
    startDate,
    endDate,
    pageNum: 1,
    pageSize: 500,
  })
    .then((list) => {
      const fetchedAt = Date.now();
      const cacheEntry: CacheEntry = { list, fetchedAt, startDate, endDate };
      memoryCache.set(key, cacheEntry);
      setStoreSliceInLocalStorage(storeId, 'businessReportDaily', {
        data: { list, startDate, endDate },
        fetchedAt,
      });
      return list;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
}

/**
 * 按店获取月度 businessReportList（dateType=1），用于「过去12个月」；带内存缓存，1 天有效
 */
export async function getBusinessReportMonthlyWithCache(
  storeId: string | number
): Promise<RevparBusinessReportItem[]> {
  const key = cacheKeyMonthly(storeId);
  const entry = memoryCacheMonthly.get(key);
  if (entry && isValid(entry)) {
    return entry.list;
  }
  const existing = inFlightMonthly.get(key);
  if (existing) return existing;

  const { startDate, endDate } = getDailyRangeInChina();
  const promise = getBusinessReportListFromThirdParty(storeId, {
    dateType: 1,
    startDate,
    endDate,
    pageNum: 1,
    pageSize: 500,
  })
    .then((list) => {
      const fetchedAt = Date.now();
      const cacheEntry: CacheEntry = { list, fetchedAt, startDate, endDate };
      memoryCacheMonthly.set(key, cacheEntry);
      return list;
    })
    .finally(() => {
      inFlightMonthly.delete(key);
    });

  inFlightMonthly.set(key, promise);
  return promise;
}

/** 从 localStorage 恢复日度 list（冷启动），仅当 key 对应「今天」且未过期时返回 */
export function getBusinessReportDailyFromLocalStorage(
  storeId: string | number
): RevparBusinessReportItem[] | null {
  try {
    const key = `ms_store_revenue_store_${String(storeId)}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { version?: number; sources?: { businessReportDaily?: { data?: { list?: RevparBusinessReportItem[] }; fetchedAt?: number }; operationAnalysis?: Record<string, { data: unknown; fetchedAt: number }> } };
    if (parsed?.version !== 1 || !parsed.sources?.businessReportDaily) return null;
    const slice = parsed.sources.businessReportDaily;
    const list = slice?.data && typeof slice.data === 'object' && Array.isArray((slice.data as { list?: RevparBusinessReportItem[] }).list)
      ? (slice.data as { list: RevparBusinessReportItem[] }).list
      : null;
    if (!list || !slice.fetchedAt) return null;
    if (Date.now() - slice.fetchedAt >= TTL_MS) return null;
    const endStr = getYesterdayStrInChina();
    const memoryKey = `businessReportDaily:${String(storeId)}:${endStr}`;
    memoryCache.set(memoryKey, { list, fetchedAt: slice.fetchedAt, startDate: '', endDate: '' });
    return list;
  } catch {
    return null;
  }
}

/** 清除某店日度/月度缓存（刷新时） */
export function clearBusinessReportDailyCache(storeId?: string | number): void {
  if (storeId != null) {
    const endStr = getYesterdayStrInChina();
    memoryCache.delete(`businessReportDaily:${String(storeId)}:${endStr}`);
    memoryCacheMonthly.delete(`businessReportMonthly:${String(storeId)}:${endStr}`);
    return;
  }
  memoryCache.clear();
  inFlight.clear();
  memoryCacheMonthly.clear();
  inFlightMonthly.clear();
}

/** 开发环境：控制台通过 window.__storeRevenueCaches.operationAnalysis 查看日度缓存 */
export function __getBusinessReportDailyCacheForDev(): Map<string, CacheEntry> {
  return memoryCache;
}
